import { Frown, Loader2, Smile, X } from 'lucide-react';
import React, { useState } from 'react';
import { db } from '../services/db';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose, userId }) => {
  const [type, setType] = useState<'good' | 'bad'>('good');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!description.trim()) return;

    setIsSubmitting(true);
    try {
      const descriptionWithVersion = `${description}\n\n[v${__APP_VERSION__}]`;
      await db.submitFeedback(userId, type, descriptionWithVersion);
      setIsSuccess(true);
      setTimeout(() => {
        onClose();
        // Reset after close
        setTimeout(() => {
            setIsSuccess(false);
            setDescription('');
            setType('good');
        }, 300);
      }, 2000);
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-brand-dark/60 backdrop-blur-sm animate-in fade-in"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-brand-cream-dark flex justify-between items-center bg-white">
           <h3 className="font-display font-extrabold text-xl text-brand-dark">Give us a Shout! 📣</h3>
           <button onClick={onClose} className="p-2 hover:bg-brand-cream-dark rounded-full"><X size={20}/></button>
        </div>

        {isSuccess ? (
            <div className="p-8 flex flex-col items-center justify-center text-center space-y-4 min-h-[300px]">
                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-3xl animate-bounce">
                    🎉
                </div>
                <h4 className="font-display font-extrabold text-xl text-brand-dark">Thanks for the shout!</h4>
                <p className="text-brand-dark-light">We've received your feedback. You're a legend.</p>
            </div>
        ) : (
            <div className="p-6 space-y-6">
                {/* Type Selection */}
                <div className="flex gap-4">
                    <button 
                        onClick={() => setType('good')}
                        className={`flex-1 p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${type === 'good' ? 'border-green-500 bg-green-50 text-green-700' : 'border-brand-cream-dark text-brand-dark-light hover:bg-brand-cream'}`}
                    >
                        <Smile size={32} />
                        <span className="font-bold text-sm">Good Vibes</span>
                    </button>
                    <button 
                        onClick={() => setType('bad')}
                        className={`flex-1 p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${type === 'bad' ? 'border-brand-red bg-red-50 text-brand-red' : 'border-brand-cream-dark text-brand-dark-light hover:bg-brand-cream'}`}
                    >
                        <Frown size={32} />
                        <span className="font-bold text-sm">Room for Improvement</span>
                    </button>
                </div>

                {/* Description */}
                <div className="space-y-2">
                    <label className="text-sm font-bold text-brand-dark">
                        {type === 'good' ? "What did we do well?" : "Where can we improve?"}
                    </label>
                    <textarea 
                        className="w-full p-4 border-2 border-brand-cream-dark rounded-xl outline-none focus:border-brand-dark min-h-[120px] resize-none bg-brand-cream/30 text-brand-dark"
                        placeholder={type === 'good' ? "Tell us what you loved..." : "Be honest, we can take it..."}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    ></textarea>
                </div>

                {/* Submit */}
                <button 
                    onClick={handleSubmit}
                    disabled={!description.trim() || isSubmitting}
                    className="w-full py-3 bg-brand-dark text-white rounded-xl font-bold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] transition active:scale-95 flex items-center justify-center gap-2"
                >
                    {isSubmitting ? <Loader2 className="animate-spin" size={20} /> : "Send Feedback"}
                </button>
            </div>
        )}
      </div>
    </div>
  );
};
